import {
  resolveDirectVideoUrl,
  tempProcessVideoSrc,
} from '@/components/DocumentPreview/utils';

const BRIGHTCOVE_PORTAL_HOSTS = ['video.cfahome.com'];

export const isBrightcovePortalUrl = (url: string): boolean => {
  try {
    return BRIGHTCOVE_PORTAL_HOSTS.includes(
      new URL(url).hostname.toLowerCase(),
    );
  } catch {
    return false;
  }
};

const isDirectBrightcovePlayerUrl = (url: string): boolean =>
  url.toLowerCase().includes('players.brightcove.net');

const isBrightcoveContentWrapperUrl = (url: string): boolean =>
  url.toLowerCase().includes('/brightcove/');

/** Whether a task link URL might resolve to an inline Brightcove embed. */
export const isBrightcoveTaskLinkUrl = (url: string): boolean => {
  if (isDirectBrightcovePlayerUrl(url) || isBrightcoveContentWrapperUrl(url)) {
    return true;
  }

  return isBrightcovePortalUrl(url);
};

/**
 * Iframe src when the task link is already a direct `players.brightcove.net`
 * URL. Returns undefined for portal and /brightcove/ wrapper links — those need
 * {@link resolveBrightcoveEmbedUrl} (async HTML fetch).
 */
export const getBrightcoveEmbedUrl = (url: string): string | undefined => {
  if (!isDirectBrightcovePlayerUrl(url)) {
    return undefined;
  }

  return tempProcessVideoSrc(url);
};

/** Resolves any Brightcove task link to an iframe src (direct or fetched). */
export const resolveBrightcoveEmbedUrl = async (
  url: string,
): Promise<string | undefined> => {
  const directPlayerEmbedUrl = getBrightcoveEmbedUrl(url);
  if (directPlayerEmbedUrl) {
    return directPlayerEmbedUrl;
  }

  if (!isBrightcoveTaskLinkUrl(url)) {
    return undefined;
  }

  const resolved = await resolveDirectVideoUrl(url);
  if (!resolved || !isDirectBrightcovePlayerUrl(resolved)) {
    return undefined;
  }

  return tempProcessVideoSrc(resolved);
};
